import { useMemo, useState } from "react";
import { Youtube } from "lucide-react";
import type { DataRecord } from "../../../types";
import { fechaCorta } from "../fechaCorta";
import { edadDias, paginasVisibles, textoEdad } from "../mensajes/mensajesUtil";
import { aspectLabel, str, toneOf, truthy } from "../sandbox/resumenData";
import type { SandboxTheme } from "../sandbox/SandboxResumen";
import "../mensajes/mensajes.css";
import "./youtube.css";

type Filtro = "pendientes" | "para_responder" | "leads" | "vencidas" | "respondidas" | "criticos" | "todos";

const POR_PAGINA = 20;
const MAX_VIDEOS = 60;
const num = (v: number) => v.toLocaleString("es-CL");
const cuando = (c: DataRecord) => str(c.created_time) || str(c.date);
// Estado calculado en el servidor con los mismos criterios de Mensajes: responder, vencida, cierre o respondida.
const estado = (c: DataRecord) => str(c.estado_atencion);
const pendiente = (c: DataRecord) => estado(c) === "responder" || estado(c) === "vencida";
const enlaceVideo = (c: DataRecord) => `https://www.youtube.com/watch?v=${encodeURIComponent(str(c.post_id))}`;
const enlaceComentario = (c: DataRecord) => `${enlaceVideo(c)}&lc=${encodeURIComponent(str(c.feedback_id))}`;
const autor = (c: DataRecord) => str(c.author_name).trim() || "Anónimo";
const inicial = (c: DataRecord) => autor(c).replace(/^@/, "").charAt(0).toUpperCase() || "?";

const FILTROS: Array<{ id: Filtro; label: string; pasa: (c: DataRecord) => boolean }> = [
  { id: "pendientes", label: "Pendientes", pasa: pendiente },
  { id: "para_responder", label: "Para responder", pasa: (c) => estado(c) === "responder" },
  { id: "leads", label: "Posibles leads", pasa: (c) => truthy(c.es_posible_lead) },
  { id: "vencidas", label: "Vencidas", pasa: (c) => estado(c) === "vencida" },
  { id: "respondidas", label: "Respondidas", pasa: (c) => estado(c) === "respondida" },
  { id: "criticos", label: "Críticos", pasa: (c) => truthy(c.critico) },
  { id: "todos", label: "Todos", pasa: () => true },
];

function Etiquetas({ c }: { c: DataRecord }) {
  const aspecto = str(c.aspecto).trim();
  return (
    <>
      {truthy(c.critico) && <span className="msj-tag lead">Crítico</span>}
      {truthy(c.es_posible_lead) && <span className="msj-tag warm">Posible lead</span>}
      {toneOf(c) === "neg" && !truthy(c.critico) && <span className="msj-tag">Negativo</span>}
      {estado(c) === "respondida" && <span className="msj-tag ok">Respondido</span>}
      {estado(c) === "responder" && <span className="msj-tag now">Para responder</span>}
      {estado(c) === "vencida" && <span className="msj-tag">Vencida</span>}
      {estado(c) === "cierre" && <span className="msj-tag">Cierre</span>}
      {aspecto && aspecto !== "otro" && <span className="msj-tag">{aspectLabel(aspecto)}</span>}
    </>
  );
}

/** Comentarios de YouTube del cliente: lista con filtros y el comentario completo al lado. */
export function SocialYoutubeComentarios({ comments, theme = "dark" }: { comments: DataRecord[]; theme?: SandboxTheme }) {
  const [elegido, setElegido] = useState<Filtro | null>(null);
  const [video, setVideo] = useState("");
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<string | null>(null);
  const [detalleAbierto, setDetalleAbierto] = useState(false);

  // La lista son los comentarios raíz; las respuestas (de terceros y del canal) se ven dentro del comentario.
  const ordenados = useMemo(
    () => comments.filter((c) => !truthy(c.is_reply) && !truthy(c.is_owner_author)).sort((a, b) => cuando(b).localeCompare(cuando(a))),
    [comments],
  );
  const respuestasDe = useMemo(() => {
    const porPadre = new Map<string, DataRecord[]>();
    for (const c of comments) {
      if (!truthy(c.is_reply)) continue;
      const padre = str(c.parent_feedback_id);
      porPadre.set(padre, [...(porPadre.get(padre) ?? []), c]);
    }
    for (const lista of porPadre.values()) lista.sort((a, b) => cuando(a).localeCompare(cuando(b)));
    return porPadre;
  }, [comments]);
  const conteos = useMemo(() => {
    const k = Object.fromEntries(FILTROS.map((f) => [f.id, ordenados.filter(f.pasa).length])) as Record<Filtro, number>;
    return k;
  }, [ordenados]);
  const videos = useMemo(() => {
    const porVideo = new Map<string, { id: string; titulo: string; n: number }>();
    for (const c of ordenados) {
      const id = str(c.post_id);
      if (!id) continue;
      const v = porVideo.get(id) ?? { id, titulo: str(c.post_title).trim() || id, n: 0 };
      v.n += 1;
      porVideo.set(id, v);
    }
    return [...porVideo.values()].sort((a, b) => b.n - a.n);
  }, [ordenados]);

  const filtro: Filtro = elegido ?? (conteos.para_responder > 0 ? "para_responder" : "pendientes");
  const pasa = FILTROS.find((f) => f.id === filtro)?.pasa ?? (() => true);
  const filtrados = useMemo(
    () => ordenados.filter((c) => pasa(c) && (!video || str(c.post_id) === video)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordenados, filtro, video],
  );
  const paginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const actual = Math.min(page, paginas);
  const visibles = filtrados.slice((actual - 1) * POR_PAGINA, actual * POR_PAGINA);
  const seleccionado = ordenados.find((c) => str(c.feedback_id) === sel) ?? null;

  const reiniciar = () => {
    setPage(1);
    setSel(null);
    setDetalleAbierto(false);
  };
  const total = ordenados.length;
  const primera = ordenados[ordenados.length - 1];

  return (
    <div className="sbx msj yt" data-theme={theme}>
      <header className="msj-head">
        <div className="msj-head-t">
          <h1 className="yt-titulo"><Youtube size={28} aria-hidden="true" />YouTube</h1>
          <p className="msj-sub">
            {num(total)} comentarios en {num(videos.length)} videos{primera ? ` · desde ${fechaCorta(cuando(primera))}` : ""}
          </p>
        </div>
      </header>

      <section className="msj-kpis" aria-label="Indicadores">
        {[
          ["Pendientes", conteos.pendientes, total > 0 ? `${Math.round((conteos.pendientes / total) * 100)}% del total` : ""],
          ["Para responder", conteos.para_responder, "recientes"],
          ["Vencidas", conteos.vencidas, "antiguas"],
          ["Posibles leads", conteos.leads, "sin respuesta"],
          ["Respondidas", conteos.respondidas, `de ${num(total)}`],
        ].map(([l, v, s]) => (
          <div key={String(l)} className="msj-kpi">
            <div className="l">{l}</div>
            <div className="v num">{num(Number(v))}<small>{s}</small></div>
          </div>
        ))}
      </section>

      <section className={`msj-panel${detalleAbierto ? " detail-open" : ""}`} aria-label="Comentarios de YouTube">
        <div className="msj-list">
          <div className="msj-chips" role="group" aria-label="Filtros">
            {FILTROS.map((f) => (
              <button key={f.id} type="button" className="msj-chip" aria-pressed={filtro === f.id} onClick={() => { setElegido(f.id); reiniciar(); }}>
                {f.label}<span className="c num">{num(conteos[f.id])}</span>
              </button>
            ))}
          </div>
          <label className="yt-video-filtro">
            <span>Video</span>
            <select value={video} onChange={(e) => { setVideo(e.target.value); reiniciar(); }}>
              <option value="">Todos los videos</option>
              {videos.slice(0, MAX_VIDEOS).map((v) => (
                <option key={v.id} value={v.id}>{v.titulo} ({num(v.n)})</option>
              ))}
            </select>
          </label>

          <div className="msj-rows">
            {visibles.length === 0 ? (
              <div className="msj-vacio">No hay comentarios en este filtro.</div>
            ) : (
              visibles.map((c) => {
                const id = str(c.feedback_id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`msj-row${truthy(c.critico) || truthy(c.es_posible_lead) ? " lead" : estado(c) === "responder" ? " now" : ""}`}
                    aria-current={sel === id}
                    onClick={() => {
                      setSel(id);
                      setDetalleAbierto(true);
                    }}
                  >
                    <span className="t">{str(c.message).trim() || "Sin texto"}</span>
                    <span className="m">
                      <span className="yt-autor">{autor(c)}</span>
                      <span aria-hidden="true">·</span>
                      <span>{textoEdad(edadDias(cuando(c)))}</span>
                      {(respuestasDe.get(id)?.length ?? 0) > 0 && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="num">{num(respuestasDe.get(id)!.length)} {respuestasDe.get(id)!.length === 1 ? "respuesta" : "respuestas"}</span>
                        </>
                      )}
                    </span>
                    <span className="yt-video-ref">{str(c.post_title).trim() || "Video sin título"}</span>
                  </button>
                );
              })
            )}
          </div>

          {filtrados.length > 0 && (
            <nav className="msj-pager" aria-label="Paginación">
              <span className="count num">{num((actual - 1) * POR_PAGINA + 1)}–{num(Math.min(filtrados.length, actual * POR_PAGINA))} de {num(filtrados.length)}</span>
              <div className="pages">
                <button type="button" className="pg" disabled={actual <= 1} aria-label="Página anterior" onClick={() => setPage(actual - 1)}>‹</button>
                {paginasVisibles(actual, paginas).map((p, i) =>
                  p === "…" ? (
                    <span key={`g${i}`} className="gap">…</span>
                  ) : (
                    <button key={p} type="button" className="pg num" aria-current={p === actual ? "page" : undefined} onClick={() => setPage(p)}>{p}</button>
                  ),
                )}
                <button type="button" className="pg" disabled={actual >= paginas} aria-label="Página siguiente" onClick={() => setPage(actual + 1)}>›</button>
              </div>
            </nav>
          )}
        </div>

        <div className="msj-detail">
          {seleccionado ? (
            <>
              <div className="msj-d-head">
                <button type="button" className="msj-btn msj-back" onClick={() => setDetalleAbierto(false)}>← Volver</button>
                <div className="who">
                  <h2>{autor(seleccionado)}</h2>
                  <div className="tags">
                    <Etiquetas c={seleccionado} />
                    <span className="msj-tag num">{fechaCorta(cuando(seleccionado))}</span>
                  </div>
                </div>
              </div>
              <div className="msj-thread">
                <article className="yt-coment">
                  <span className="yt-avatar" aria-hidden="true">{inicial(seleccionado)}</span>
                  <div className="yt-coment-c">
                    <div className="yt-coment-a">{autor(seleccionado)}<span>{textoEdad(edadDias(cuando(seleccionado)))}</span></div>
                    <p>{str(seleccionado.message).trim() || "Sin texto"}</p>
                  </div>
                </article>
                {(respuestasDe.get(str(seleccionado.feedback_id))?.length ?? 0) > 0 && (
                  <section className="yt-respuestas" aria-label="Respuestas">
                    <h3>{num(respuestasDe.get(str(seleccionado.feedback_id))!.length)} {respuestasDe.get(str(seleccionado.feedback_id))!.length === 1 ? "respuesta" : "respuestas"}</h3>
                    {respuestasDe.get(str(seleccionado.feedback_id))!.map((r) => (
                      <article key={str(r.feedback_id)} className="yt-coment yt-resp">
                        <span className={`yt-avatar${truthy(r.is_owner_author) ? " canal" : ""}`} aria-hidden="true">{inicial(r)}</span>
                        <div className="yt-coment-c">
                          <div className="yt-coment-a">
                            {autor(r)}
                            {truthy(r.is_owner_author) && <em className="yt-canal">Canal</em>}
                            <span>{textoEdad(edadDias(cuando(r)))}</span>
                          </div>
                          <p>{str(r.message).trim() || "Sin texto"}</p>
                        </div>
                      </article>
                    ))}
                  </section>
                )}
                <a className="yt-video-card" href={enlaceVideo(seleccionado)} target="_blank" rel="noopener noreferrer">
                  <span className="yt-play" aria-hidden="true"><Youtube size={18} /></span>
                  <span className="yt-video-t">{str(seleccionado.post_title).trim() || "Ver el video"}</span>
                  <span className="yt-video-l">Ver video ↗</span>
                </a>
              </div>
              <div className="msj-d-foot">
                <span>Solo lectura. Para responder, abre el comentario en YouTube.</span>
                <a href={enlaceComentario(seleccionado)} target="_blank" rel="noopener noreferrer">Abrir en YouTube ↗</a>
              </div>
            </>
          ) : (
            <div className="msj-vacio">Elige un comentario de la lista para verlo aquí.</div>
          )}
        </div>
      </section>
    </div>
  );
}
